/**
 * V14: 部署加载遮罩 — 提交出战到战场就绪全链路的统一过渡动画
 *
 * 覆盖场景：
 * - 屏 2 preflight（Pyodide 冷启动 + LLM auto-fix 管线）
 * - 屏 4 引擎初始化（PyodideDualRuntime 双 Worker 加载）
 *
 * 单例模式：全局共享一个 overlay（挂 document.body），跨屏导航不销毁。
 * 屏 2 创建 → preflight 更新 → 屏 4 渲染 → engine.start() 完成后 fade out
 *
 * Ardot v2.0：圆角 0 / 描边 1-2px / 锐角 / monospace / 无渐变无阴影
 */

type Stage = "submitting" | "preflight" | "auto_fix" | "init_engine" | "level_load";

interface LoadingState {
  el: HTMLElement | null;
  statusEl: HTMLElement | null;
  progressEl: HTMLElement | null;
  detailEl: HTMLElement | null;
  visible: boolean;
  scanTimer: ReturnType<typeof setInterval> | null;
}

const state: LoadingState = {
  el: null,
  statusEl: null,
  progressEl: null,
  detailEl: null,
  visible: false,
  scanTimer: null,
};

const STAGE_TEXT: Record<Stage, string> = {
  submitting: "部署指令已提交...",
  preflight: "代码检测中...",
  auto_fix: "AI 自动修复中...",
  init_engine: "战场引擎初始化...",
  level_load: "关卡资源加载中...",
};

function createOverlay(): HTMLElement {
  if (state.el) return state.el;

  const el = document.createElement("div");
  el.className = "deploy-loading-overlay";
  el.innerHTML = `
    <div class="deploy-loading-frame">
      <div class="deploy-loading-border deploy-loading-border--top"></div>
      <div class="deploy-loading-border deploy-loading-border--right"></div>
      <div class="deploy-loading-border deploy-loading-border--bottom"></div>
      <div class="deploy-loading-border deploy-loading-border--left"></div>
      <div class="deploy-loading-inner">
        <div class="deploy-loading-scanline"></div>
        <div class="deploy-loading-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="4" width="40" height="40" stroke="currentColor" stroke-width="1.5" stroke-dasharray="80" stroke-dashoffset="80">
              <animate attributeName="stroke-dashoffset" from="80" to="0" dur="1.5s" fill="freeze" repeatCount="indefinite"/>
            </rect>
            <rect x="12" y="12" width="24" height="24" stroke="currentColor" stroke-width="1" opacity="0.5"/>
            <line x1="24" y1="4" x2="24" y2="16" stroke="currentColor" stroke-width="2">
              <animate attributeName="y2" values="16;10;16" dur="0.8s" repeatCount="indefinite"/>
            </line>
            <line x1="4" y1="24" x2="16" y2="24" stroke="currentColor" stroke-width="2">
              <animate attributeName="x2" values="16;10;16" dur="0.8s" repeatCount="indefinite"/>
            </line>
            <line x1="44" y1="24" x2="32" y2="24" stroke="currentColor" stroke-width="2">
              <animate attributeName="x2" values="32;38;32" dur="0.8s" repeatCount="indefinite"/>
            </line>
            <line x1="24" y1="44" x2="24" y2="32" stroke="currentColor" stroke-width="2">
              <animate attributeName="y2" values="32;38;32" dur="0.8s" repeatCount="indefinite"/>
            </line>
          </svg>
        </div>
        <div class="deploy-loading-status">部署指令已提交...</div>
        <div class="deploy-loading-detail"></div>
        <div class="deploy-loading-progress">
          <div class="deploy-loading-progress-bar"></div>
        </div>
      </div>
    </div>
  `;

  state.el = el;
  state.statusEl = el.querySelector(".deploy-loading-status");
  state.detailEl = el.querySelector(".deploy-loading-detail");
  state.progressEl = el.querySelector(".deploy-loading-progress-bar");
  return el;
}

function startScanAnimation(el: HTMLElement) {
  stopScanAnimation();
  let frame = 0;
  state.scanTimer = setInterval(() => {
    frame = (frame + 1) % 100;
    el.style.setProperty("--scan-pos", `${frame}%`);
  }, 30);
}

function stopScanAnimation() {
  if (state.scanTimer !== null) {
    clearInterval(state.scanTimer);
    state.scanTimer = null;
  }
}

/**
 * 显示加载遮罩
 * @param stage 当前阶段
 * @param detail 可选详细信息（如第几轮修复）
 */
export function showDeployLoading(stage: Stage = "submitting", detail?: string) {
  const el = createOverlay();
  document.body.appendChild(el);

  requestAnimationFrame(() => {
    el.classList.add("deploy-loading-overlay--visible");
    startScanAnimation(el);
  });

  state.visible = true;
  updateDeployLoading(stage, detail);
}

/**
 * 更新加载状态
 */
export function updateDeployLoading(stage: Stage, detail?: string) {
  if (!state.visible) {
    showDeployLoading(stage, detail);
    return;
  }

  if (state.statusEl) {
    state.statusEl.textContent = STAGE_TEXT[stage];
  }
  if (state.detailEl) {
    state.detailEl.textContent = detail ?? "";
  }
  if (state.progressEl && stage === "init_engine") {
    state.progressEl.classList.add("deploy-loading-progress-bar--engine");
  }
}

/**
 * 隐藏加载遮罩（fade out + remove）
 */
export function hideDeployLoading() {
  if (!state.el || !state.visible) return;

  state.visible = false;
  state.el.classList.add("deploy-loading-overlay--hidden");
  stopScanAnimation();

  setTimeout(() => {
    state.el?.remove();
    state.el = null;
    state.statusEl = null;
    state.detailEl = null;
    state.progressEl = null;
  }, 350);
}

/**
 * 检查加载遮罩是否正在显示
 */
export function isDeployLoadingVisible(): boolean {
  return state.visible;
}

// ===== 安全兜底：hash 路由切离 battle 时自动隐藏 =====
// 防止浏览器后退 / 手动 hash 切换时遮罩残留
window.addEventListener("hashchange", () => {
  if (state.visible && !window.location.hash.startsWith("#/battle/") && !window.location.hash.startsWith("#/editor/")) {
    hideDeployLoading();
  }
});
